import { useUser } from '@/src/store/user.context';
import { addIf } from '@/src/utils/list.utils';
import { parseUrl } from '@/src/utils/url.utils';
import {
  Badge,
  Button,
  Container,
  FormField,
  Header,
  Input,
  KeyValuePairs,
  Link,
  SpaceBetween,
  TokenGroup,
} from '@amzn/awsui-components-react';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChallengePropAction, useCreateChallenge } from '../../../../store/create-challenge.context';
import { Challenge, ChallengeUtils } from '../../../../types/Challenge';
import { i18nKeys } from '../../../../utils/i18n.utils';
import ChallengeStatusIndicator from '../../challengesCommon/ChallengeStatusIndictator';

interface ChallengeSummaryDetailProp {
  challenge: Challenge;
}

const ChallengeTags: React.FC<ChallengeSummaryDetailProp> = ({ challenge }) => {
  const tagItems: { label: string; dismissLabel: string }[] = [];

  const { t } = useTranslation();
  const { editMode, editedChallenge, handleUpdateChallengeProp } = useCreateChallenge();
  const [tags, setTags] = useState<{ label: string; dismissLabel: string }[]>(tagItems);
  const [value, setValue] = React.useState('');

  useEffect(() => {
    const challengeTags = editMode ? editedChallenge?.props.tags : challenge.props.tags;

    if (challengeTags) {
      challengeTags.forEach((tag: string) => {
        tagItems.push({
          label: tag,
          dismissLabel: `${t(i18nKeys.challenges.challengeDetails.labels.removeTag)}: ${tag}`,
        });
      });
    }
    setTags(tagItems);
  }, [challenge, editMode]);

  const addTag = () => {
    const newTags = tags;
    newTags.push({
      label: value,
      dismissLabel: `${t(i18nKeys.challenges.challengeDetails.labels.removeTag)}: ${value}`,
    });
    const updatedTags = newTags.map((tag) => tag.label);
    handleUpdateChallengeProp(ChallengePropAction.TAGS, updatedTags);
    setTags(newTags);
    setValue('');
  };

  const removeTag = (itemIndex: number) => {
    const newTags = [...tags.slice(0, itemIndex), ...tags.slice(itemIndex + 1)];
    setTags(newTags);
    const updatedTags = newTags.map((tag) => tag.label);
    handleUpdateChallengeProp(ChallengePropAction.TAGS, updatedTags);
  };

  if (editMode) {
    return (
      <SpaceBetween size={'s'}>
        <TokenGroup onDismiss={({ detail: { itemIndex } }) => removeTag(itemIndex)} items={tags} />
        <Input onChange={({ detail }) => setValue(detail.value)} value={value} data-testid="challenge-add-tag-input" />
        <Button onClick={addTag} data-testid="challenge-add-tag-button">
          {t(i18nKeys.challenges.challengeDetails.buttons.addTag)}
        </Button>
      </SpaceBetween>
    );
  }

  if (!challenge.props.tags?.length) {
    return <>-</>;
  }

  return (
    <SpaceBetween size="xxs" direction="horizontal">
      {challenge.props.tags.map((tag, key) => {
        return <Badge key={key}>{tag}</Badge>;
      })}
    </SpaceBetween>
  );
};

const ChallengeLcsrTicket: React.FC<ChallengeSummaryDetailProp> = ({ challenge }) => {
  const { t } = useTranslation();
  const { editMode, editedChallenge, handleUpdateChallengeProp } = useCreateChallenge();
  const [lcsrTicket, setLcsrTicket] = useState<string>(editedChallenge?.props.lcsrTicket || '');
  const [errorText, setErrorText] = useState<string | undefined>();

  useEffect(() => {
    setLcsrTicket(challenge.props.lcsrTicket || '');
    setErrorText(undefined);
  }, [challenge, editMode]);

  const updateLcsrTicket = (value: string) => {
    setLcsrTicket(value);
    handleUpdateChallengeProp(ChallengePropAction.LCSR_TICKET, value);

    const url = parseUrl(value);

    if (!url) {
      setErrorText(t(i18nKeys.challenges.challengeDetails.field.lcsrTicket.invalidUrl));
    } else if (url.hostname !== 't.corp.amazon.com') {
      setErrorText(t(i18nKeys.challenges.challengeDetails.field.lcsrTicket.invalidUrl));
    } else if (url.pathname.length < 2 || url.pathname.split('/').length !== 2) {
      setErrorText(t(i18nKeys.challenges.challengeDetails.field.lcsrTicket.invalidUrlOnlyTicket));
    } else {
      setErrorText(undefined);
    }
  };

  if (editMode) {
    return (
      <FormField
        errorText={errorText}
        constraintText={t(i18nKeys.challenges.challengeDetails.field.lcsrTicket.constraint)}
      >
        <Input
          onChange={({ detail }) => updateLcsrTicket(detail.value)}
          value={lcsrTicket || ''}
          placeholder={t(i18nKeys.challenges.challengeDetails.field.lcsrTicket.placeholder)}
          data-testid="challenge-lcsr-ticket"
        />
      </FormField>
    );
  }

  return (
    <>
      {lcsrTicket ? (
        <Link href={lcsrTicket} external data-testid="challenge-lcsr-link">
          {lcsrTicket}
        </Link>
      ) : (
        '-'
      )}
    </>
  );
};

const ChallengeSummaryDetail: React.FC<ChallengeSummaryDetailProp> = ({ challenge }) => {
  const { t } = useTranslation();
  const { user } = useUser();

  const showLcsrTicket = ChallengeUtils.isCollaborator(challenge, user);

  return (
    <Container
      header={
        <Header variant="h2" data-testid="summary-title">
          {t(i18nKeys.challenges.challengeDetails.headings.summary)}
        </Header>
      }
    >
      <KeyValuePairs
        columns={3}
        items={[
          { label: t(i18nKeys.challenges.challengeDetails.titles.id), value: challenge.challengeId },
          ...addIf(!!challenge?.props?.lastEditedBy, {
            label: t(i18nKeys.challenges.challengeDetails.titles.lastMinorEdit),
            value: challenge.props.lastEditedBy,
          }),
          {
            label: t(i18nKeys.challenges.challengeDetails.titles.version),
            value: (
              <>
                <ChallengeStatusIndicator challenge={challenge} subtitle={''} showVersion />
                {!!challenge.isDefective && (
                  <SpaceBetween size="xxs">
                    <Badge color="red">{t(i18nKeys.challenges.challengeDetails.messages.defective)}</Badge>
                  </SpaceBetween>
                )}
              </>
            ),
          },
          ...addIf(!!challenge?.props?.lastApprovedBy, {
            label: t(i18nKeys.challenges.challengeDetails.titles.lastApprovedBy),
            value: challenge.props.lastApprovedBy,
          }),
          {
            label: t(i18nKeys.challenges.challengeDetails.titles.lastModified),
            value: moment(challenge.createdDate).format('lll'),
          },
          {
            label: t(i18nKeys.challenges.challengeDetails.titles.tags),
            value: <ChallengeTags challenge={challenge} />,
          },
          ...addIf(!!challenge?.props?.lastUpdatedBy, {
            label: t(i18nKeys.challenges.challengeDetails.titles.lastMajorEdit),
            value: challenge.props.lastUpdatedBy,
          }),
          ...addIf(showLcsrTicket, {
            label: t(i18nKeys.challenges.challengeDetails.titles.lcsrTicket),
            value: <ChallengeLcsrTicket challenge={challenge} />,
          }),
        ]}
      />
    </Container>
  );
};

export default ChallengeSummaryDetail;
